import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { documentCostService } from '../../services/DocumentCostService';
import { estimateReadingTime, estimateWritingTime, estimateReviewTime } from '../../utils/documentTimeEstimation';
import { roles } from '../../data/roles';
import { formatGBP } from '../../utils/formatGBP';
import { getRandomInstruction } from '../../utils/instructions';
import AttendeePicker from '../../components/AttendeePicker';
import DocumentShareButton from '../../components/DocumentShareButton';
import { compactRainbowShareButtonStyles } from '../../theme/shareButtonStyles';

export default function DocumentForm() {
  const theme = useTheme();
  
  // Document details
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('simple');
  const [contentLength, setContentLength] = useState('');
  
  // Time estimates
  const [writingTime, setWritingTime] = useState('');
  const [reviewCycles, setReviewCycles] = useState(1);
  
  // Role counts for each activity
  const [authorCounts, setAuthorCounts] = useState(
    roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {})
  );
  const [reviewerCounts, setReviewerCounts] = useState(
    roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {})
  );
  const [readerCounts, setReaderCounts] = useState(
    roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {})
  );
  
  // Results
  const [result, setResult] = useState(null);
  const [displayTotal, setDisplayTotal] = useState(0);
  const [instruction] = useState(getRandomInstruction());
  
  // Auto-calculated estimates
  const [estimatedReadingTime, setEstimatedReadingTime] = useState(0);
  const [estimatedWritingTime, setEstimatedWritingTime] = useState(0);
  const [estimatedReviewTime, setEstimatedReviewTime] = useState(0);

  const handleAuthorChange = (roleKey, value) => {
    setAuthorCounts((prev) => ({
      ...prev,
      [roleKey]: value,
    }));
  };

  const handleReviewerChange = (roleKey, value) => {
    setReviewerCounts((prev) => ({
      ...prev,
      [roleKey]: value,
    }));
  };

  const handleReaderChange = (roleKey, value) => {
    setReaderCounts((prev) => ({
      ...prev,
      [roleKey]: value,
    }));
  };

  // Auto-estimate times when content length or type changes
  useEffect(() => {
    if (contentLength) {
      const wordCount = parseInt(contentLength) || 0;
      
      // Update reading time estimate
      const readingHours = estimateReadingTime(wordCount);
      setEstimatedReadingTime(readingHours);
      
      // Update writing time estimate if not manually set
      if (!writingTime) {
        const writingHours = estimateWritingTime(wordCount, documentType);
        setEstimatedWritingTime(writingHours);
      }
      
      // Update review time estimate
      const actualWritingTime = writingTime ? parseFloat(writingTime) : estimatedWritingTime;
      const reviewHours = estimateReviewTime(actualWritingTime);
      setEstimatedReviewTime(reviewHours);
    } else {
      setEstimatedReadingTime(0);
      setEstimatedWritingTime(0);
      setEstimatedReviewTime(0);
    }
  }, [contentLength, documentType, writingTime, estimatedWritingTime]);

  // Calculate cost automatically when inputs change
  useEffect(() => {
    if (!contentLength) {
      setResult(null);
      setDisplayTotal(0);
      return;
    }

    const authors = roles.map((role) => ({
      count: authorCounts[role.key],
      hourlyRate: role.hourlyRate,
    })).filter(author => author.count > 0);

    const reviewers = roles.map((role) => ({
      count: reviewerCounts[role.key],
      hourlyRate: role.hourlyRate,
    })).filter(reviewer => reviewer.count > 0);

    const readers = roles.map((role) => ({
      count: readerCounts[role.key],
      hourlyRate: role.hourlyRate,
    })).filter(reader => reader.count > 0);

    const totalParticipants = 
      Object.values(authorCounts).reduce((sum, count) => sum + count, 0) +
      Object.values(reviewerCounts).reduce((sum, count) => sum + count, 0) +
      Object.values(readerCounts).reduce((sum, count) => sum + count, 0);

    if (totalParticipants > 0) {
      const actualWritingTime = writingTime ? parseFloat(writingTime) : estimatedWritingTime;
      
      const input = {
        authors,
        reviewers,
        readers,
        writingTimeHours: actualWritingTime,
        reviewTimeHours: estimatedReviewTime,
        readingTimeHours: estimatedReadingTime,
        reviewCycles: Number(reviewCycles) || 1,
      };

      const output = documentCostService.calculateCost(input);
      setResult(output);
      setDisplayTotal(output.totalCost);
    } else {
      setResult(null);
      setDisplayTotal(0);
    }
  }, [
    authorCounts, 
    reviewerCounts, 
    readerCounts, 
    writingTime, 
    reviewCycles, 
    estimatedWritingTime, 
    estimatedReviewTime, 
    estimatedReadingTime,
    contentLength
  ]);

  const handleReset = () => {
    setDocumentTitle('');
    setDocumentType('simple');
    setContentLength('');
    setWritingTime('');
    setReviewCycles(1);
    setAuthorCounts(roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {}));
    setReviewerCounts(roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {}));
    setReaderCounts(roles.reduce((acc, role) => ({ ...acc, [role.key]: 0 }), {}));
    setResult(null);
    setDisplayTotal(0);
    setEstimatedReadingTime(0);
    setEstimatedWritingTime(0);
    setEstimatedReviewTime(0);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mb: 2,
          textAlign: 'center',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 700,
        }}
      >
        Document Cost Calculator
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        sx={{
          mb: 3,
          color: 'text.secondary',
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
        }}
      >
        {instruction}
      </Typography>

      {/* Total Cost Display */}
      <Box
        mb={3}
        p={2}
        sx={{
          borderRadius: 4,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
            : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
          border: theme.palette.mode === 'dark' 
            ? '2px solid rgba(255,255,255,0.1)'
            : '2px solid rgba(25, 118, 210, 0.12)',
          textAlign: 'center',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleReset}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            fontSize: '0.8rem',
            minWidth: 'auto',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: '#ff6b6b',
            color: 'white',
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#ff5252',
              transform: 'scale(1.05)',
              boxShadow: '0 2px 8px rgba(255, 107, 107, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          Reset
        </Button>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            mb: 2,
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}
        >
          Total Cost
        </Typography>
        
        <Typography 
          variant="h2" 
          sx={{ 
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
            mb: 1,
            fontFamily: 'monospace',
            fontWeight: 'bold',
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: 1,
          }}
        >
          {formatGBP(displayTotal)}
        </Typography>
        
        {/* Cost Breakdown */}
        {result && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Writing Cost</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatGBP(result.breakdown.writingCost)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Review Cost</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                {formatGBP(result.breakdown.reviewCost)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Reading Cost</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatGBP(result.breakdown.readingCost)}
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box sx={{ minHeight: '28px', mt: 1, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {result && (
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {result.snarkyQuip}
            </Typography>
          )}
        </Box>
        
        {/* Share Button */}
        {result && displayTotal > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <DocumentShareButton
              documentTitle={documentTitle}
              documentType={documentType}
              contentLength={contentLength}
              authors={authorCounts}
              reviewers={reviewerCounts}
              readers={readerCounts}
              writingTimeHours={writingTime}
              reviewTimeHours={reviewCycles * (estimateReviewTime(contentLength, documentType) || 0)}
              readingTimeHours={estimateReadingTime(contentLength, documentType) || 0}
              reviewCycles={reviewCycles}
              finalCost={displayTotal}
              breakdown={result.breakdown}
              variant="contained"
              size="medium"
              sx={compactRainbowShareButtonStyles}
            />
          </Box>
        )}
      </Box>

      <Grid container rowSpacing={3}>
        {/* Document Details */}
        <Grid size={{ xs: 12}}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Comic Neue", "Comic Sans MS", cursive', fontWeight: 600 }}>
              Document Details
            </Typography>
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Document Title"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="e.g., API Documentation, User Guide..."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={documentType}
                    label="Document Type"
                    onChange={(e) => setDocumentType(e.target.value)}
                  >
                    <MenuItem value="simple">Simple</MenuItem>
                    <MenuItem value="user_guide">User Guide</MenuItem>
                    <MenuItem value="technical">Technical</MenuItem>
                    <MenuItem value="policy">Policy</MenuItem>
                    <MenuItem value="specification">Specification</MenuItem>
                    <MenuItem value="research">Research</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Content Length (words)"
                  type="number"
                  value={contentLength}
                  onChange={(e) => setContentLength(e.target.value)}
                  inputProps={{ min: 0 }}
                  required
                  error={!contentLength || contentLength === '0'}
                  helperText={
                    !contentLength || contentLength === '0' 
                      ? 'Content length is required for calculations'
                      : contentLength && estimatedReadingTime > 0 
                        ? `Estimated reading time: ${Math.round(estimatedReadingTime * 60)} min` 
                        : 'Enter word count to see estimates'
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Writing Time (hours)"
                  type="number"
                  value={writingTime}
                  onChange={(e) => setWritingTime(e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText={estimatedWritingTime > 0 && !writingTime ? 
                    `Estimated writing time: ${estimatedWritingTime.toFixed(1)} hours` : 
                    'Leave blank for auto-estimate'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Review Cycles"
                  type="number"
                  value={reviewCycles}
                  onChange={(e) => setReviewCycles(Number(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                  helperText="Number of review rounds"
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Authors Section */}
        <Grid size={{ xs: 12}}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }} data-testid="authors-section">
            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Comic Neue", "Comic Sans MS", cursive', fontWeight: 600 }}>
              📝 Authors
            </Typography>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {roles.map((role) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.key}>
                  <AttendeePicker
                    label={role.label}
                    value={authorCounts[role.key]}
                    onChange={(value) => handleAuthorChange(role.key, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Reviewers Section */}
        <Grid size={{ xs: 12}}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }} data-testid="reviewers-section">
            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Comic Neue", "Comic Sans MS", cursive', fontWeight: 600 }}>
              🔍 Reviewers
            </Typography>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {roles.map((role) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.key}>
                  <AttendeePicker
                    label={role.label}
                    value={reviewerCounts[role.key]}
                    onChange={(value) => handleReviewerChange(role.key, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Readers Section */}
        <Grid size={{ xs: 12}}>
          <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2 }} data-testid="readers-section">
            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Comic Neue", "Comic Sans MS", cursive', fontWeight: 600 }}>
              📖 Readers
            </Typography>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {roles.map((role) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.key}>
                  <AttendeePicker
                    label={role.label}
                    value={readerCounts[role.key]}
                    onChange={(value) => handleReaderChange(role.key, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}